import { Ban, Pipette } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ANNOTATION_INK_COLORS, ANNOTATION_TINT_COLORS, toColorInputValue } from './annotation-palette';
import { AnnotationSwatchGrid } from './annotation-swatch-grid';
import { TOOLBAR_BORDER, TOOLBAR_ICON_SIZE, toolbarPaintBackgroundStyle, toolbarPanelHeadingStyle } from './toolbar-styles';
import { ToolbarButton } from './toolbar-button';
import { ToolbarPopover } from './toolbar-popover';
import { useRecentAnnotationColors } from '../sku-annotation-context';

/** Trailing delay after the last picker frame before the single model write lands */
const COMMIT_DELAY_MS = 220;

const openEyeDropper = (onPick: (color: string) => void): void => {
	if (!('EyeDropper' in window)) return;
	const eyeDropper = new (window as unknown as { EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> } }).EyeDropper();
	eyeDropper.open().then((result) => onPick(result.sRGBHex)).catch((): null => null);
};

type AnnotationCustomColorRowProps = {
	title: string;
	value: string | null;
	onInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onFlush: () => void;
	onPick: (color: string) => void;
};

/** The off-palette footer: the native picker (previewed locally, committed once) and the screen eyedropper */
const AnnotationCustomColorRow = (props: AnnotationCustomColorRowProps): React.ReactElement => {
	const { title, value, onInput, onFlush, onPick } = props;

	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${TOOLBAR_BORDER}` }}>
			<input
				type="color"
				data-native-focus="true"
				value={toColorInputValue(value, '#000000')}
				onChange={onInput}
				onBlur={onFlush}
				title={`Custom ${title.toLowerCase()}`}
				style={{ width: '26px', height: '22px', padding: 0, border: `1px solid ${TOOLBAR_BORDER}`, borderRadius: '4px', background: 'none', cursor: 'pointer' }}
			/>
			<span style={{ flex: 1, userSelect: 'none' }}>Custom</span>
			{'EyeDropper' in window ? (
				<ToolbarButton title="Pick a colour from the screen" onClick={() => openEyeDropper(onPick)}>
					<Pipette size={TOOLBAR_ICON_SIZE - 2} />
				</ToolbarButton>
			) : null}
		</div>
	);
};

export type AnnotationColorPopoverProps = {
	title: string;
	/** Short word shown beside the swatch, so the control names itself instead of relying on the icon */
	label: string;
	/** Inner content of the trigger button, drawn above a bar showing the current colour */
	icon: React.ReactNode;
	/** `null` MEANS none -- this channel is not painted */
	value: string | null;
	onChange: (color: string | null) => void;
	/** Uncommitted feedback while the native picker is dragged; called with null to clear it */
	onPreview?: (color: string | null) => void;
	/** Caption for the none swatch; absent means this channel cannot be turned off */
	noneLabel?: string;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
};

/**
 * ONE paint channel: a colour, or none. Every channel (fill, border/line, text, highlight) is this same
 * control -- a curated palette where one click is one commit, plus a custom picker that previews in local
 * state and writes the model ONCE after the drag stops. Nothing here writes per pointer frame (#390).
 */
export const AnnotationColorPopover = (props: AnnotationColorPopoverProps): React.ReactElement => {
	const { title, label, icon, value, onChange, onPreview, noneLabel, isOpen, onOpenChange } = props;
	const recentColors = useRecentAnnotationColors();
	const [preview, setPreview] = useState<string | null>(null);
	const pendingRef = useRef<string | null>(null);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const flushRef = useRef<(() => void) | null>(null);

	const clearTimer = useCallback((): void => {
		if (!timerRef.current) return;
		clearTimeout(timerRef.current);
		timerRef.current = null;
	}, []);

	const commit = useCallback((color: string | null): void => {
		clearTimer();
		pendingRef.current = null;
		setPreview(null);
		onPreview?.(null);
		onChange(color);
	}, [clearTimer, onChange, onPreview]);

	const flush = useCallback((): void => {
		const pending = pendingRef.current;
		if (pending === null) {
			clearTimer();
			return;
		}
		commit(pending);
	}, [clearTimer, commit]);

	flushRef.current = flush;

	useEffect(() => () => flushRef.current?.(), []);

	const handleCustomInput = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
		const next = e.target.value;
		pendingRef.current = next;
		setPreview(next);
		onPreview?.(next);
		clearTimer();
		timerRef.current = setTimeout(() => flushRef.current?.(), COMMIT_DELAY_MS);
	}, [clearTimer, onPreview]);

	const handleOpenChange = useCallback((nextOpen: boolean): void => {
		if (!nextOpen) flush();
		onOpenChange(nextOpen);
	}, [flush, onOpenChange]);

	const shownColor = preview ?? value;

	return (
		<ToolbarPopover
			title={title}
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			panelWidth={168}
			label={label}
			trigger={(
				<span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
					{icon}
					<span style={{
						width: '15px',
						height: '3px',
						borderRadius: '2px',
						border: `1px solid ${TOOLBAR_BORDER}`,
						...toolbarPaintBackgroundStyle(shownColor, 4)
					}} />
				</span>
			)}
		>
			{recentColors.length > 0 ? (
				<>
					<div style={toolbarPanelHeadingStyle}>Recent</div>
					<div style={{ marginBottom: '8px' }}>
						<AnnotationSwatchGrid colors={recentColors} selected={preview ? null : value} onPick={commit} />
					</div>
				</>
			) : null}

			<div style={toolbarPanelHeadingStyle}>Palette</div>
			<AnnotationSwatchGrid colors={ANNOTATION_INK_COLORS} selected={preview ? null : value} onPick={commit} />
			<div style={{ height: '4px' }} />
			<AnnotationSwatchGrid colors={ANNOTATION_TINT_COLORS} selected={preview ? null : value} onPick={commit} />

			{noneLabel ? (
				<div style={{ marginTop: '8px' }}>
					<ToolbarButton title={noneLabel} label={noneLabel} isActive={value === null} onClick={() => commit(null)}>
						<Ban size={TOOLBAR_ICON_SIZE - 2} />
					</ToolbarButton>
				</div>
			) : null}

			<AnnotationCustomColorRow title={title} value={preview ?? value} onInput={handleCustomInput} onFlush={flush} onPick={commit} />
		</ToolbarPopover>
	);
};
